import { ReactNode, useState } from 'react';
import type { HighwayMaintenanceFeePenaltyTicket } from '#/components/ui/tickets/highway-maintenance-fee-penalty/model';
import {
  ActionIcon,
  Box,
  Button,
  Divider,
  Drawer,
  Flex,
  Group,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { HighwayMaintenanceFeePenaltySummary } from './HighwayMaintenanceFeePenaltySummary';
import { HighwayMaintenanceFeePenaltyNotesList } from './HighwayMaintenanceFeePenaltyNotesList';
import { PaginationNav } from './PaginationNav';
import { useAddOperatorNote } from '../../endpoints/ticketsHooks';

const NoteInput = ({
  onSubmit,
  isLoading,
}: {
  onSubmit: (input: string) => void;
  isLoading: boolean;
}) => {
  const [value, setValue] = useState('');

  const handleSubmit = () => {
    if (!value.trim()) return;
    onSubmit(value.trim());
    setValue('');
  };

  return (
    <Stack gap="xs">
      <Textarea
        placeholder="新增備註..."
        value={value}
        onChange={(e) => setValue(e.currentTarget.value)}
        minRows={3}
      />
      <Group justify="flex-end">
        <Button size="xs" loading={isLoading} onClick={handleSubmit}>
          提交
        </Button>
      </Group>
    </Stack>
  );
};

type DetailPanelProps = {
  id: HighwayMaintenanceFeePenaltyTicket['id'] | undefined;
  currentIndex: number | undefined;
  total: number;
  setCurrentDetailIndex: (index: number | undefined) => void;
  paymentId: string | null | undefined;
};

const Block = ({ children }: { children: ReactNode }) => {
  return (
    <Box bg={'rgb(255, 255, 255)'} bdrs={'lg'} className="shadow-lg" p="md">
      {children}
    </Box>
  );
};

export const HighwayMaintenanceFeePenaltyTicketDetailPanel = ({
  id,
  currentIndex,
  total,
  setCurrentDetailIndex,
  paymentId,
}: DetailPanelProps) => {
  const { mutate: addOperatorNote, isPending: isAddingOperatorNote } =
    useAddOperatorNote();

  const opened = id !== undefined;
  const onClose = () => setCurrentDetailIndex(undefined);

  const handleCreateNote = (content: string) => {
    if (!id) return;
    addOperatorNote({ id, content });
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="xl"
      withCloseButton={false}
      styles={{ body: { padding: 0, height: '100%' } }}
    >
      <Flex direction={'column'} h={'100%'}>
        <Flex gap="md" justify="space-between" p="sm">
          <ActionIcon variant="subtle" color="gray" onClick={onClose}>
            <IconX size={16} />
          </ActionIcon>
          <PaginationNav
            currentIndex={currentIndex ?? 0}
            setIndex={setCurrentDetailIndex}
            total={total}
          />
        </Flex>
        <Stack gap="md" bg={'rgb(248, 249, 250)'} p="md" className="grow-1">
          <Block>
            <HighwayMaintenanceFeePenaltySummary
              id={id}
              paymentId={paymentId}
            />
          </Block>
          <Block>
            <Stack>
              <Text fw={600}>任務歷程</Text>
              <NoteInput
                onSubmit={handleCreateNote}
                isLoading={isAddingOperatorNote}
              />
              <Divider />
              <HighwayMaintenanceFeePenaltyNotesList id={id} />
            </Stack>
          </Block>
        </Stack>
      </Flex>
    </Drawer>
  );
};
